import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Admin, AdminLoginData } from '@/types/admin';
import { login as apiLogin, getProfile as apiGetProfile, logout as apiLogout } from '@/services/auth.service';
import { STORAGE_KEYS } from '@/config/constants';

export interface AuthState {
  token: string | null;
  user: Admin | null;
  isLoading: boolean;
  error: string | null;
  
  login: (username: string, password: string) => Promise<void>;
  fetchProfile: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  setToken: (token: string) => void;
}

export const useAuthStore = create<AuthState>()(
  immer((set, get) => ({
    // ✅ 初始化时从 localStorage 读取 token，但 user 保持 null，等待 fetchProfile 填充
    token: localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN),
    user: null,
    isLoading: false,
    error: null,

    login: async (username: string, password: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const response = await apiLogin({ username, password });
        const data: AdminLoginData = response.data.data!;
        
        localStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, data.access_token);
        
        set((state) => {
          state.token = data.access_token;
          state.isLoading = false;
        });

        await get().fetchProfile();

      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : '登录失败';
        set((state) => {
          state.error = errorMessage;
          state.isLoading = false;
        });
        throw err;
      }
    },

    fetchProfile: async () => {
      const currentToken = get().token;
      // ✅ 如果没有 token，直接返回，避免无效请求
      if (!currentToken) return;

      try {
        const response = await apiGetProfile();
        const user: Admin = response.data.data!;
        
        set((state) => {
          state.user = user;
        });
      } catch (err: unknown) {
        console.error('Failed to fetch profile:', err);
        // ✅ 如果获取失败（通常是因为 token 过期或无效），清除本地状态
        get().logout();
      }
    },

    logout: async () => {
      try {
        await apiLogout();
      } catch {
        console.warn('Logout API call failed, clearing local state anyway');
      } finally {
        localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
        set((state) => {
          state.token = null;
          state.user = null;
          state.error = null;
        });
      }
    },

    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },

    setToken: (token: string) => {
      localStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, token);
      set((state) => {
        state.token = token;
      });
    },
  }))
);